/**
 * FILE MANAGER SERVICE
 *
 * API service functions for file upload management.
 * Handles generating presigned URLs and uploading files to S3.
 */

import { fetcher } from "@/lib/fetcher";

// File types supported by the API
export type FileType = "IMAGE" | "DOCUMENT";

// MIME types mapping
export const MIME_TYPES = {
  // Images
  "image/jpeg": "jpeg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",

  // Documents
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/plain": "txt",
  "application/json": "json",
  "application/x-yaml": "yaml",
  "application/yaml": "yaml",
  "text/yaml": "yaml",
  "text/x-yaml": "yaml",
} as const;

// Request interface for generating upload URL
export interface GenerateUploadUrlRequest {
  fileName: string;
  mime: string;
  fileType: FileType;
  fileExtend: string;
  folder?: string; // Optional folder path (e.g., "avatars", "documents/user-123")
}

// Response interface for upload URL generation
export interface UploadUrlData {
  uploadURL: string;
  viewURL: string;
}

export type ApiResponse<T> = { status?: string; data: T[]; message?: string };
export type GenerateUploadUrlResponse = ApiResponse<UploadUrlData>;

// Upload result interface
export interface FileUploadResult {
  success: boolean;
  fileUrl?: string;
  viewUrl?: string;
  error?: string;
  message?: string;
}

/**
 * Generate presigned URL for file upload
 */
export async function generateUploadUrl(
  request: GenerateUploadUrlRequest
): Promise<GenerateUploadUrlResponse> {
  return fetcher<GenerateUploadUrlResponse>(
    "/api/ai-transform/v1/file-manager/generate-url",
    {
      method: "POST",
      body: JSON.stringify(request),
    }
  );
}

/**
 * Upload file to S3 using presigned URL
 */
export async function uploadFileToS3(
  uploadUrl: string,
  file: File
): Promise<{ success: boolean; error?: string }> {
  try {
    // Check if presigned URL expects x-amz-acl header
    const expectsAcl = uploadUrl.includes("x-amz-acl");

    const headers: Record<string, string> = {
      "Content-Type": file.type,
    };

    // Only add x-amz-acl if the presigned URL expects it AND bucket supports ACLs
    if (expectsAcl) {
      // Don't add the header - let it fail and backend should fix the presigned URL generation
    }

    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers,
      mode: "cors", // Explicitly set CORS mode
      credentials: "omit", // Don't send credentials for S3
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "No error details");
      console.error("❌ S3 upload failed:", {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new Error(
        `Upload failed with status: ${response.status} - ${response.statusText}`
      );
    }

    return { success: true };
  } catch (error) {
    console.error("❌ S3 upload failed:", error);

    // Handle specific ORB error
    if (
      error instanceof Error &&
      error.message.includes("ERR_BLOCKED_BY_ORB")
    ) {
      return {
        success: false,
        error:
          "Upload blocked by browser security policy. Please check CORS configuration.",
      };
    }

    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to upload file to S3",
    };
  }
}

/**
 * Get file extension from MIME type
 */
export function getFileExtensionFromMime(mimeType: string): string {
  return MIME_TYPES[mimeType as keyof typeof MIME_TYPES] || "bin";
}

/**
 * Determine file type from MIME type
 */
export function getFileTypeFromMime(mimeType: string): FileType {
  if (mimeType.startsWith("image/")) {
    return "IMAGE";
  }
  return "DOCUMENT";
}

/**
 * Validate file before upload
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file size (max 20MB)
  const maxSize = 20 * 1024 * 1024; // 20MB
  if (file.size > maxSize) {
    return {
      valid: false,
      error: "File size must be less than 20MB",
    };
  }

  // Check if MIME type is supported
  if (!(file.type in MIME_TYPES)) {
    return {
      valid: false,
      error: "File type not supported",
    };
  }

  return { valid: true };
}

/**
 * Complete file upload process
 * 1. Validate file
 * 2. Generate presigned URL
 * 3. Upload to S3
 * 4. Return file URL
 */
export async function uploadFile(
  file: File,
  folder?: string
): Promise<FileUploadResult> {
  try {
    // Step 1: Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }

    // Step 2: Prepare request data
    const fileExtension = getFileExtensionFromMime(file.type);
    const fileType = getFileTypeFromMime(file.type);

    const request: GenerateUploadUrlRequest = {
      fileName: file.name,
      mime: file.type,
      fileType,
      fileExtend: fileExtension,
      folder, // Add folder to request if provided
    };

    // Step 3: Generate presigned URL
    const urlResponse = await generateUploadUrl(request);

    if (!urlResponse.data || urlResponse.data.length === 0) {
      return {
        success: false,
        error: "Failed to generate upload URL",
      };
    }

    const uploadInfo = urlResponse.data[0] as UploadUrlData;
    const uploadURL = uploadInfo.uploadURL;
    const viewURL = uploadInfo.viewURL;

    // Step 4: Upload file to S3 (with multiple fallback strategies)
    const uploadResult = await uploadFileToS3(uploadURL, file);

    if (!uploadResult.success) {
      return {
        success: false,
        error: uploadResult.error,
      };
    }

    // Step 5: Return success with file URLs
    return {
      success: true,
      fileUrl: viewURL,
      viewUrl: viewURL,
      message: "File uploaded successfully",
    };
  } catch (error) {
    console.error("❌ File upload failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to upload file",
    };
  }
}

/**
 * Upload multiple files
 */
export async function uploadMultipleFiles(
  files: File[],
  folder?: string
): Promise<FileUploadResult[]> {
  const results: FileUploadResult[] = [];

  for (const file of files) {
    const result = await uploadFile(file, folder);
    results.push(result);
  }

  return results;
}
